import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, getPinToken, setPinToken, clearPinToken } from "@/lib/api";

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasPin, setHasPin] = useState(null);   // null = unknown yet, true/false once known
  const [pinLocked, setPinLocked] = useState(false); // true = google session valid, PIN not yet entered this app-open

  const checkAuth = useCallback(async () => {
    try {
      // Step 1: is there a valid Google session at all?
      const statusRes = await api.get("/auth/pin-status");
      setHasPin(statusRes.data.has_pin);

      if (!statusRes.data.has_pin) {
        // No PIN configured yet — logged in via Google, needs to set one up first.
        setPinLocked(true);
        setUser(null);
        setLoading(false);
        return;
      }

      // Step 2: has this browser tab already unlocked with the PIN?
      if (!getPinToken()) {
        setPinLocked(true);
        setUser(null);
        setLoading(false);
        return;
      }

      // Step 3: fetch the actual user (requires a valid, unexpired PIN token).
      const r = await api.get("/auth/me");
      setUser(r.data);
      setPinLocked(false);
    } catch (err) {
      if (err?.response?.data?.detail === "pin_required") {
        clearPinToken();
        setPinLocked(true);
        setUser(null);
      } else {
        setUser(null);
        setPinLocked(false);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  // An ID token straight from Google. The server verifies it against Google's
  // signing keys and mints its own session; this used to bounce the user
  // through a third party's login page, and adopt the session token that party
  // handed back — see backend src/google-auth.js.
  const loginGoogle = async (credential) => {
    await api.post("/auth/google", { credential });
    await checkAuth();
  };

  // Email + password. After the session cookie is set we run the same
  // post-auth flow as Google (PIN setup/verify), so both paths converge.
  const loginPassword = async (email, password) => {
    await api.post("/auth/login", { email, password });
    await checkAuth();
  };

  const register = async (email, password, name) => {
    await api.post("/auth/register", { email, password, name });
    await checkAuth();
  };

  const changePassword = async (currentPassword, newPassword) => {
    await api.post("/auth/change-password", {
      current_password: currentPassword,
      new_password: newPassword,
    });
    return true;
  };

  // Fetch the user after unlocking. If /auth/me fails (e.g. a transient error),
  // roll the just-stored PIN token back so we never end up "half unlocked" —
  // which would otherwise show "Incorrect PIN" for a correct PIN while the token
  // silently lingers in storage.
  const finishUnlock = async (pinToken) => {
    setPinToken(pinToken);
    try {
      const me = await api.get("/auth/me");
      setUser(me.data);
      setPinLocked(false);
      return me.data;
    } catch (e) {
      clearPinToken();
      setUser(null);
      throw e;
    }
  };

  const setupPin = async (pin) => {
    const r = await api.post("/auth/pin-setup", { pin });
    setHasPin(true);
    return finishUnlock(r.data.pin_token);
  };

  const verifyPin = async (pin) => {
    const r = await api.post("/auth/pin-verify", { pin });
    return finishUnlock(r.data.pin_token);
  };

  const changePin = async (currentPin, newPin) => {
    const r = await api.post("/auth/pin-change", { current_pin: currentPin, new_pin: newPin });
    setPinToken(r.data.pin_token);
    return true;
  };

  const lockApp = async () => {
    try {
      await api.post("/auth/pin-lock");
    } catch (err) {
      // Non-fatal — we still lock the UI locally even if the network call fails.
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.debug("[auth] pin-lock request failed:", err?.message);
      }
    }
    clearPinToken();
    setUser(null);
    setPinLocked(true);
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      // Server-side session cleanup may fail (e.g. network); we still clear
      // the client state so the user is logged out locally.
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.debug("[auth] logout request failed:", err?.message);
      }
    }
    clearPinToken();
    setUser(null);
    setPinLocked(false);
    window.location.href = "/login";
  };

  return (
    <AuthCtx.Provider value={{
      user, setUser, loading, hasPin, pinLocked,
      loginGoogle, loginPassword, register, changePassword,
      setupPin, verifyPin, changePin, lockApp, logout,
      refresh: checkAuth,
    }}>
      {children}
    </AuthCtx.Provider>
  );
}
